// Behaviour for Osano's "opt-out honored" notice — the persistent bottom-left
// pill (`.osano-cm-opt-out-honored-wrapper`) and its tooltip. Distinct from the
// cookie-preferences tab (`.osano-cm-widget`) hidden in theme.ts globalStyles.
//
// The 10s auto-fade is pure CSS (theme.ts globalStyles). This module adds the
// two pieces that need script: the "Hide for N days" CTA injected into Osano's
// tooltip, and the persisted, TTL-bounded dismissal that keeps the notice hidden.
//
// Osano injects that pill + tooltip at runtime, so we don't own the DOM. Every
// touch here is defensive: if Osano renames its markup the CTA simply never
// appears and nothing else is affected.

// Stores the epoch-ms the dismissal EXPIRES at (not a boolean) — the notice
// comes back once it lapses.
export const OPT_OUT_NOTICE_HIDDEN_KEY = 'splash.optOutNotice.hidden';

// A dismissal lasts 30 days, then the notice reappears.
export const OPT_OUT_NOTICE_TTL_DAYS = 30;
export const OPT_OUT_NOTICE_TTL_MS = OPT_OUT_NOTICE_TTL_DAYS * 24 * 60 * 60 * 1000;

// Toggled on <html>; theme.ts globalStyles display:none's the wrapper while it is present.
export const OPT_OUT_NOTICE_HIDDEN_CLASS = 'splash-opt-out-notice-hidden';

// Osano CMP class names, verified against the live GPC widget's DOM (2026-08-28).
// Note the BEM shapes: the tooltip content is an ELEMENT (`__tooltip-content`),
// while `--tooltip-active` (theme.ts) is a MODIFIER — the minified bundle made
// these easy to confuse, and the wrong separator silently defeats the CTA.
export const OSANO_OPT_OUT_WRAPPER_CLASS = 'osano-cm-opt-out-honored-wrapper';
export const OSANO_OPT_OUT_TOOLTIP_CONTENT_CLASS =
  'osano-cm-opt-out-honored-wrapper__tooltip-content';

export const HIDE_NOTICE_BUTTON_CLASS = 'splash-opt-out-notice__hide';
// Derived from the TTL so the copy can't drift from the actual duration.
export const HIDE_NOTICE_LABEL = `Hide for ${OPT_OUT_NOTICE_TTL_DAYS} days`;

export function isOptOutNoticeHidden(): boolean {
  try {
    const raw = localStorage.getItem(OPT_OUT_NOTICE_HIDDEN_KEY);
    if (raw === null) return false;
    const expiresAt = Number(raw);
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true;
    // Expired, or a stale/legacy non-timestamp value → clear it and show again.
    localStorage.removeItem(OPT_OUT_NOTICE_HIDDEN_KEY);
    return false;
  } catch {
    // Private mode / blocked storage → treat as not dismissed.
    return false;
  }
}

export function hideOptOutNotice(): void {
  // Root class first: the visual hide must land even if storage is unavailable.
  document.documentElement.classList.add(OPT_OUT_NOTICE_HIDDEN_CLASS);
  try {
    localStorage.setItem(OPT_OUT_NOTICE_HIDDEN_KEY, String(Date.now() + OPT_OUT_NOTICE_TTL_MS));
  } catch {
    // Blocked storage: hidden for this page, may reappear next load.
  }
}

export function injectHideCta(tooltipContent: Element, onHide: () => void): void {
  try {
    if (tooltipContent.querySelector(`.${HIDE_NOTICE_BUTTON_CLASS}`)) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = HIDE_NOTICE_BUTTON_CLASS;
    button.textContent = HIDE_NOTICE_LABEL;
    button.addEventListener('click', (event) => {
      // Keep the click off Osano's wrapper handler, which toggles the tooltip.
      event.stopPropagation();
      onHide();
    });
    tooltipContent.appendChild(button);
  } catch {
    // Osano markup changed under us — degrade silently.
  }
}

export function installOptOutNoticeControl(): () => void {
  const noop = (): void => {};
  try {
    if (isOptOutNoticeHidden()) {
      document.documentElement.classList.add(OPT_OUT_NOTICE_HIDDEN_CLASS);
      return noop;
    }

    let observer: MutationObserver | undefined;

    const onHide = (): void => {
      hideOptOutNotice();
      observer?.disconnect();
    };

    const injectInto = (root: Element | Document): void => {
      if (root instanceof Element && root.matches(`.${OSANO_OPT_OUT_TOOLTIP_CONTENT_CLASS}`)) {
        injectHideCta(root, onHide);
      }
      for (const content of root.querySelectorAll(`.${OSANO_OPT_OUT_TOOLTIP_CONTENT_CLASS}`)) {
        injectHideCta(content, onHide);
      }
    };

    // Osano may already have painted a widget before this runs.
    injectInto(document);

    // Watch document.body but only ever examine INSERTED nodes, so per-mutation
    // cost is proportional to what's added, not a document rescan. Deliberately
    // NOT narrowed to one wrapper: Osano renders two opt-out-honored wrappers
    // (the floating window pill + the in-dialog one) and (re)renders the tooltip
    // subtree — incl. `__tooltip-content` — into a wrapper each time it opens, so
    // the observer has to keep watching for those insertions.
    // `typeof` is the throw-safe test for a maybe-absent global.
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof Element) injectInto(node);
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => observer?.disconnect();
  } catch {
    return noop;
  }
}
